import { FC, ReactElement } from 'react';

import { AdjustedOpeningHours, OpeningHours } from '../../../../../entities/Store/Store';
import { getStoreOpeningStatus } from '../../../../../helpers/storeDayOpeningStatus';
import { useFormatDate, useTrans } from '../../../../../hooks';
import { DialogWithButton } from '../../../../DialogWithButton/DialogWithButton';

import './StoreHours.scss';

interface StoreHoursProps {
    openingHours: OpeningHours[];
    adjustedOpeningHours: AdjustedOpeningHours[];
    className?: string;
}

export const StoreHours: FC<StoreHoursProps> = ({
    openingHours,
    adjustedOpeningHours,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const formatDate = useFormatDate();

    const columns = [
        openingHours.slice(0, 4),
        openingHours.slice(4),
    ];

    return (
        <div className={`store-hours ${className}`}>
            <h4 className="store-hours__status">
                {getStoreOpeningStatus(openingHours, adjustedOpeningHours)}
            </h4>

            <div className="store-hours__opening-hours">
                {columns.map(column => (
                    <div key={column[0].day} className="store-hours__opening-hours-column">
                        {column.map(({ day, from, to }) => {
                            const dayLabel = `${trans(`date.days.${day}`)}: `;

                            const hoursValue = `${from?.substring(0, 5)} - ${to?.substring(0, 5)} ${trans('date.hour')}`;

                            return (
                                <div key={day}>
                                    {dayLabel}

                                    {hoursValue}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {adjustedOpeningHours.length > 0 && (
                <DialogWithButton
                    buttonText={trans('containers.storeDetail.showAdjustedOpeningHours')}
                    className="store-hours__adjusted-opening-hours-dialog"
                >
                    {adjustedOpeningHours.map(({
                        name,
                        day,
                        from,
                        to,
                        closed,
                    }) => {
                        const formattedDay = formatDate(day, {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                        });

                        const dayLabel = name
                            ? `${formattedDay} (${name}) `
                            : formattedDay;

                        const description = closed
                            ? trans('common.closed')
                            : `${from?.substring(0, 5)} - ${to?.substring(0, 5)} ${trans('date.hour')}`;

                        return (
                            <div key={day} className="store-hours__adjusted-opening-hours-column">
                                <p className="store-hours__adjusted-opening-hours-column-day">
                                    {dayLabel}
                                </p>

                                <p>
                                    {description}
                                </p>
                            </div>
                        );
                    })}
                </DialogWithButton>
            )}
        </div>
    );
};
